import React, { useState } from 'react';
import {
  AlertTriangle, CheckCircle2, Clock, Download, FileAudio, Loader2, Mic, RotateCcw, Smartphone, Trash2, Users,
} from 'lucide-react';
import ConfirmDialog from '../ConfirmDialog';
import { DatiAudioVerbale, RiepilogoAudio, urlAssolutoAudio } from '../../services/verbaliAudioService';
import { formattaMs, useUrlStabile } from '../../utils/verbaliTrascrizione';
import {
  nomeAudio, testoAudioEliminato, testoConservazione, testoInterrotta, TESTO_PARTI_MANCANTI, TESTO_REGISTRA_TELEFONO,
  TESTO_TELEFONO_FERMO, TESTO_TRASCRIZIONE_PARZIALE, testoTrascrizioneInCorso,
} from './testiAudio';

interface VerbaleAudioCardProps {
  dati: DatiAudioVerbale;
  onRiprova: (audioId: string) => void;
  onElimina: (audioId: string) => void;
  onApriVoci: () => void;
  /** Audio che questa scheda sta registrando (`registrazione.audioId` del hook): non si elimina. */
  audioInRegistrazione?: string | null;
}

/** Riprese già ricomposte: da qui i file scaricabili sono definitivi. */
const STATI_DOPO_TERMINA = new Set(['completa', 'in_trascrizione', 'trascritta', 'errore']);

function Stato({ a }: { a: RiepilogoAudio }) {
  const base = 'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium';
  if (a.eliminatoAt && a.stato !== 'trascritta') {
    return <span className={`${base} bg-slate-100 text-slate-600 dark:bg-gray-700 dark:text-gray-300`}>Audio eliminato</span>;
  }
  switch (a.stato) {
    case 'in_registrazione':
      if (a.interrottaAlle) {
        return <span className={`${base} bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200`}>{testoInterrotta(a.interrottaAlle)}</span>;
      }
      return a.dispositivo === 'app'
        ? (
          <span className={`${base} bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300`}>
            <Smartphone className="h-3 w-3" aria-hidden="true" />
            {TESTO_REGISTRA_TELEFONO}
          </span>
        )
        : <span className={`${base} bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300`}>In registrazione</span>;
    case 'in_caricamento':
      return <span className={`${base} bg-slate-100 text-slate-700 dark:bg-gray-700 dark:text-gray-200`}>Caricamento non completato</span>;
    case 'completa':
      return (
        <span className={`${base} bg-slate-100 text-slate-700 dark:bg-gray-700 dark:text-gray-200`}>
          <Clock className="h-3 w-3" aria-hidden="true" />
          In coda per la trascrizione
        </span>
      );
    case 'in_trascrizione':
      return (
        <span className={`${base} bg-emerald-50 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200`}>
          <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
          {testoTrascrizioneInCorso(a.avanzamento.fatti, a.avanzamento.totali)}
        </span>
      );
    case 'trascritta':
      return (
        <span className={`${base} bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200`}>
          <CheckCircle2 className="h-3 w-3" aria-hidden="true" />
          {a.senzaVoci ? 'Trascritta, senza voci' : 'Trascritta'}
        </span>
      );
    case 'errore':
      return <span className={`${base} bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300`}>Errore</span>;
    default:
      return null;
  }
}

/** Lettore di un audio con l'URL firmato tenuto fermo (l'elenco riletto porta URL nuovi). */
function Lettore({ audioId, url }: { audioId: string; url: string | null }) {
  const [usato, rinnova] = useUrlStabile(audioId, url);
  const [guasto, setGuasto] = useState(false);
  if (!usato) return null;
  if (guasto) {
    return (
      <p className="mt-2 text-xs text-amber-800 dark:text-amber-200">
        L'audio non si carica: il collegamento potrebbe essere scaduto, ricarica la pagina.
      </p>
    );
  }
  return (
    // eslint-disable-next-line jsx-a11y/media-has-caption
    <audio
      controls
      preload="metadata"
      src={usato}
      onError={() => { if (!rinnova()) setGuasto(true); }}
      className="mt-2 h-9 w-full"
    />
  );
}

/** Un riquadro per audio: stato, durata, avanzamento, ascolto, download, conservazione, voci, eliminazione. */
const VerbaleAudioCard: React.FC<VerbaleAudioCardProps> = ({
  dati, onRiprova, onElimina, onApriVoci, audioInRegistrazione = null,
}) => {
  const [daEliminare, setDaEliminare] = useState<RiepilogoAudio | null>(null);
  const elenco = [...(dati.audio || [])].sort((a, b) => (a.ordine || 0) - (b.ordine || 0));

  return (
    <section aria-label="Audio della riunione" className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800">
      <div className="mb-2 flex items-center gap-2">
        <Mic className="h-4 w-4 text-emerald-600 dark:text-emerald-400" aria-hidden="true" />
        <span className="text-sm font-semibold text-slate-900 dark:text-white">Registrazione</span>
      </div>
      {elenco.length === 0 && (
        <p className="text-xs text-slate-500 dark:text-gray-400">Nessun audio: registra la riunione o carica un file.</p>
      )}
      <ul className="space-y-3">
        {elenco.map((a) => {
          const eliminato = !!a.eliminatoAt;
          const url = !eliminato ? urlAssolutoAudio(a.urlAscolto) : null;
          const download = !eliminato ? a.urlDownload : [];
          const pctTrascrizione = a.avanzamento.totali > 0 ? Math.round((a.avanzamento.fatti / a.avanzamento.totali) * 100) : 0;
          // Una registrazione ancora aperta (qui o in un'altra scheda) non si elimina: prima «Termina».
          const eliminabile = a.stato !== 'in_registrazione' && a._id !== audioInRegistrazione;
          // Audio scaduto (anche a metà trascrizione): ritentare non può riuscire.
          const riprovabile = !eliminato && !a.trascrizioneParziale && a.errore?.codice !== 'AUDIO_SCADUTO';
          const partiMancanti = STATI_DOPO_TERMINA.has(a.stato) && download.length > 0 && download.length < a.riprese.length;
          return (
            <li key={a._id} className="rounded-xl border border-slate-100 bg-slate-50/60 p-3 dark:border-gray-700 dark:bg-gray-900/30">
              <div className="flex items-start gap-2">
                {a.origine === 'file'
                  ? <FileAudio className="mt-0.5 h-4 w-4 flex-shrink-0 text-slate-500 dark:text-gray-400" aria-hidden="true" />
                  : <Mic className="mt-0.5 h-4 w-4 flex-shrink-0 text-slate-500 dark:text-gray-400" aria-hidden="true" />}
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-slate-900 dark:text-white" title={nomeAudio(a)}>{nomeAudio(a)}</p>
                  <div className="mt-1 flex flex-wrap items-center gap-2">
                    <Stato a={a} />
                    {a.stato === 'in_registrazione' && a.interrottaAlle && a.dispositivo === 'app' && (
                      <span className="block w-full text-xs text-amber-800 dark:text-amber-200">{TESTO_TELEFONO_FERMO}</span>
                    )}
                    {a.durataMs ? (
                      <span className="text-xs tabular-nums text-slate-500 dark:text-gray-400">{formattaMs(a.durataMs)}</span>
                    ) : null}
                  </div>
                </div>
                {eliminabile && (
                  <button
                    type="button"
                    onClick={() => setDaEliminare(a)}
                    className="inline-flex flex-shrink-0 items-center gap-1 rounded-md px-1.5 py-1 text-xs font-medium text-slate-500 hover:bg-red-50 hover:text-red-600 dark:text-gray-400 dark:hover:bg-red-900/20 dark:hover:text-red-400"
                  >
                    <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                    Elimina
                  </button>
                )}
              </div>

              {a.stato === 'in_trascrizione' && a.avanzamento.totali > 0 && (
                <div
                  className="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-200 dark:bg-gray-700"
                  role="progressbar"
                  aria-label="Avanzamento della trascrizione"
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={pctTrascrizione}
                >
                  <div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${pctTrascrizione}%` }} />
                </div>
              )}

              {a.stato === 'errore' && a.errore && (
                <div className="mt-2 flex items-start gap-2 rounded-lg bg-red-50 px-2.5 py-2 text-xs text-red-700 dark:bg-red-900/20 dark:text-red-300">
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
                  <span className="min-w-0 flex-1">{a.errore.messaggio || 'Trascrizione non riuscita'}</span>
                  {riprovabile && (
                    <button
                      type="button"
                      onClick={() => onRiprova(a._id)}
                      className="inline-flex flex-shrink-0 items-center gap-1 font-semibold text-red-800 hover:underline dark:text-red-200"
                    >
                      <RotateCcw className="h-3 w-3" aria-hidden="true" />
                      Riprova
                    </button>
                  )}
                </div>
              )}

              {a.trascrizioneParziale && (
                <p className="mt-2 flex items-start gap-2 rounded-lg bg-amber-50 px-2.5 py-2 text-xs font-medium text-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
                  {TESTO_TRASCRIZIONE_PARZIALE}
                </p>
              )}

              <Lettore audioId={a._id} url={url} />

              {download.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1">
                  {download.map((d) => (
                    <a
                      key={d.ripresa}
                      href={urlAssolutoAudio(d.url) || undefined}
                      download
                      className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 hover:underline dark:text-emerald-300"
                    >
                      <Download className="h-3.5 w-3.5" aria-hidden="true" />
                      {download.length === 1 && a.riprese.length <= 1 ? 'Scarica' : `Scarica parte ${d.ripresa + 1}`}
                    </a>
                  ))}
                </div>
              )}
              {partiMancanti && <p className="mt-1 text-xs text-slate-500 dark:text-gray-400">{TESTO_PARTI_MANCANTI}</p>}

              {eliminato ? (
                <p className="mt-2 text-xs text-slate-500 dark:text-gray-400">{testoAudioEliminato(a.eliminatoAt as string)}</p>
              ) : a.scadeIl ? (
                <p className="mt-2 text-xs text-slate-500 dark:text-gray-400">{testoConservazione(a.scadeIl)}</p>
              ) : null}

              {a.stato === 'trascritta' && !a.senzaVoci && (
                <button
                  type="button"
                  onClick={onApriVoci}
                  className={`mt-2 inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-semibold ${
                    a.vociIncerte > 0
                      ? 'bg-amber-50 text-amber-800 hover:bg-amber-100 dark:bg-amber-900/25 dark:text-amber-200 dark:hover:bg-amber-900/40'
                      : 'bg-emerald-50 text-emerald-800 hover:bg-emerald-100 dark:bg-emerald-900/25 dark:text-emerald-200 dark:hover:bg-emerald-900/40'
                  }`}
                >
                  <Users className="h-3.5 w-3.5" aria-hidden="true" />
                  {a.vociIncerte > 0 ? `Voci: ${a.vociIncerte} da associare` : 'Voci: associa i nomi'}
                </button>
              )}
            </li>
          );
        })}
      </ul>

      <ConfirmDialog
        isOpen={!!daEliminare}
        onClose={() => setDaEliminare(null)}
        onConfirm={() => { if (daEliminare) onElimina(daEliminare._id); }}
        title="Eliminare questo audio?"
        message="Si eliminano il file, la sua trascrizione, i momenti segnati e le voci associate. Il testo del verbale resta com'è."
        confirmText="Elimina"
        type="danger"
      />
    </section>
  );
};

export default VerbaleAudioCard;
